import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";

import { AppDispatch, RootState }      from "../../redux/store";
import { addToCart, decrementCartItem, removeFromCart } from "../../redux/cartSlice";
import { listenToProductData, listenToPublishedProducts } from "../../redux/productSlice";
import { GENERAL_COLOR_ATTRIBUTE_ID, productColors, Sizes } from "../../CATEGORIES/ATTRIBUTES/ProductsAttributes";

import dhlLogo     from "../../assets/delivery/dhl.png";
import fbaLogo      from "../../assets/delivery/fba.png";
import fbsLogo      from "../../assets/delivery/fbs.png";
import safariLogo   from "../../assets/delivery/xafarii.png";

import {
  ArrowLeft, ShoppingBag, Trash2, Heart,
  CreditCard, Package, Truck,
  Lock, AlertCircle, X, Check, ChevronRight,
  Mail, Home as HomeIcon, ChevronDown, Users, Ruler, Smartphone,
} from "lucide-react";

import countries, { CountryInfo } from "../../GLOBAL/Countries";
import { nearestDeliveryLocation } from "../../redux/commercePartnersSlice";
import { calculateProductsEarnings } from "../../redux/productServiceFeeSlice";



/* ═══════════════════════════════════════════════════════════
   HEARTLAS DESIGN TOKENS — identical to ProductsHome
   (crimson red, warm gold/orange, near-black outline, cream bg)
═══════════════════════════════════════════════════════════ */
const HEART = {
  bg: "#FBF6EE",
  panelBg: "#FFFFFF",

  dark: "#1B0E06",
  black: "#1B0E06",
  textMuted: "#7A6653",
  textFaint: "#A6947F",

  red: "#C8202E",
  redDark: "#9E1521",
  redSoft: "#FBE7E9",

  gold: "#F2A93E",
  goldDark: "#D4900F",
  goldSoft: "#FCEFD4",

  border: "#EEDFC4",
  divider: "#F3E6CE",

  green: "#15803D",
};

const T = {
  bg:        HEART.bg,
  bgSoft:    HEART.goldSoft,
  bgMuted:   HEART.border,
  ink:       HEART.dark,
  inkMid:    HEART.textMuted,
  muted:     HEART.textMuted,
  mutedSoft: HEART.textFaint,
  border:    HEART.border,
  borderMid: "rgba(200,32,46,0.28)",
  gold:      HEART.gold,
  goldDark:  HEART.goldDark,
  red:       HEART.red,
  redDark:   HEART.redDark,
  green:     HEART.green,
  grad:      `linear-gradient(135deg, ${HEART.red} 0%, ${HEART.gold} 100%)`,
  gradH:     `linear-gradient(90deg, ${HEART.red} 0%, ${HEART.gold} 100%)`,
};

const GRAD_H = T.gradH;

const CHECKOUT_URL          = "https://europe-west3-cosfabe.cloudfunctions.net/createCheckoutSession";
const COSMOOLA_CHECKOUT_URL = "https://europe-west3-armerix.cloudfunctions.net/createCosmoolaCheckoutSession";
const DHL_RATES_URL         = "https://europe-west3-armerix.cloudfunctions.net/getDhlRates";
const XAFARII_RATES_URL     = "https://europe-west3-armerix.cloudfunctions.net/getXafariilRates";
const FBA_RATES_URL         = "https://europe-west3-armerix.cloudfunctions.net/getFbaRates";
const CREATE_CUSTOMER_URL   = "https://europe-west3-armerix.cloudfunctions.net/createCustomerAccount";

type CreateCustomerResponse = { userId: string };

const FIT_OPTIONS = ["Regular","Slim","Relaxed","Oversized","Tailored","Petite","Plus Size"];

/* ── Sizes catalogue — normalized to a plain array ── */
const SIZE_VALUES: { id: number; name: string }[] = Array.from((Sizes as any).values());

/* ── helpers ── */
const fmtPrice = (n: number, sym = "€") =>
  `${sym}${Number(n ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const getColorAttr = (id?: number) => {
  if (!id) return null;
  const grp = productColors.find(g => g.id === GENERAL_COLOR_ATTRIBUTE_ID);
  return grp?.values?.find(v => v.id === id) ?? null;
};

const getSizeName = (id?: number) => {
  if (!id) return null;
  return SIZE_VALUES.find((v: any) => v.id === id)?.name ?? null;
};

function sphere3D(hex: string): string {
  return [
    `radial-gradient(circle at 35% 32%, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0.10) 28%, transparent 52%),`,
    `radial-gradient(circle at 68% 72%, rgba(0,0,0,0.30) 0%, transparent 55%),`,
    `radial-gradient(circle at 50% 50%, ${hex} 0%, ${hex} 100%)`,
  ].join(" ");
}

/* Only clothing/shoe items (i.e. items that carry a real fashion size)
   are considered "fashion" — everything else never sees size UI. */
const isFashionItem = (item: any, listing: any) => {
  if (item.sizeId) {
    const fashionMatch = SIZE_VALUES.find((v: any) => v.id === item.sizeId);
    if (fashionMatch) return true;
  }
  const sizeIds: number[] = item.colorId
    ? (listing?.variants?.[item.colorId]?.sizeIds ?? [])
    : [];
  return sizeIds.some(sid => SIZE_VALUES.find((v: any) => v.id === sid));
};

const cartItemKey = (item: any) => `${item.listingId}-${item.colorId ?? "nc"}-${item.sizeId ?? "ns"}`;

const getAvailableSizesForItem = (item: any, listing: any): { id: number; name: string }[] => {
  let ids: number[] | undefined;
  if (item.colorId && listing?.variants?.[item.colorId]?.sizeIds?.length) {
    ids = listing.variants[item.colorId].sizeIds;
  } else if (listing?.sizeIds?.length) {
    ids = listing.sizeIds;
  }
  if (ids && ids.length) {
    return ids
      .map((id: number) => SIZE_VALUES.find((v: any) => v.id === id))
      .filter(Boolean) as { id: number; name: string }[];
  }
  return SIZE_VALUES;
};

function flagSphere(emoji: string) {
  return (
    <div style={{ position: "relative", width: 22, height: 22, flexShrink: 0 }}>
      <div style={{ position: "absolute", inset: 0, borderRadius: "50%", background: "radial-gradient(circle at 35% 32%, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0.10) 28%, transparent 52%), radial-gradient(circle at 68% 72%, rgba(0,0,0,0.18) 0%, transparent 55%)", zIndex: 1, pointerEvents: "none" }} />
      <div style={{ width: 22, height: 22, borderRadius: "50%", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, lineHeight: 1, background: T.bgSoft, userSelect: "none" }}>{emoji}</div>
    </div>
  );
}

function stripDialCode(raw: string, dialCode: string): string {
  const trimmed = raw.trim();
  if (trimmed.startsWith(dialCode)) return trimmed.slice(dialCode.length).trim();
  for (const c of countries) {
    if (trimmed.startsWith(c.phoneCode)) return trimmed.slice(c.phoneCode.length).trim();
  }
  return trimmed.replace(/^\+/, "");
}

/* ═══════════════════════════════════════════════════════════
   GLOBAL CSS — cute, rounded, coral-red / warm-gold checkout
═══════════════════════════════════════════════════════════ */
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;1,300;1,400&family=Montserrat:wght@300;400;500;600;700;800;900&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
  button,select,input,textarea{font-family:inherit;cursor:pointer;}
  ::-webkit-scrollbar{display:none;}
  input::placeholder{color:rgba(27,14,6,.35);}
  @keyframes pcSpin{to{transform:rotate(360deg);}}
  @keyframes gradSpin{0%{background-position:0% 50%;}50%{background-position:100% 50%;}100%{background-position:0% 50%;}}
  @keyframes slideUp{from{opacity:0;transform:translateY(10px);}to{opacity:1;transform:translateY(0);}}

  .ci-row{background:#fff;border:1.5px solid ${T.border};border-radius:22px;overflow:hidden;transition:box-shadow .22s,transform .22s;margin-bottom:14px;}
  .ci-row:hover{box-shadow:0 4px 10px rgba(27,14,6,.05),0 20px 40px rgba(27,14,6,.10);transform:translateY(-3px);}

  .ci-thumb{border-radius:18px;overflow:hidden;position:relative;box-shadow:inset 0 0 0 1px rgba(238,223,196,.6), 0 8px 20px rgba(27,14,6,.10);}

  .ib{width:32px;height:32px;border:1.5px solid ${T.border};background:#fff;border-radius:12px;display:flex;align-items:center;justify-content:center;cursor:pointer;transition:all .15s;}
  .ib:hover{border-color:${T.gold};background:${T.bgSoft};}
  .ib.danger:hover{border-color:${T.red}!important;background:${HEART.redSoft}!important;}

  .pay-btn{width:100%;padding:15px 0;border:none;border-radius:999px;font-size:13px;font-weight:800;letter-spacing:.05em;font-family:inherit;display:flex;align-items:center;justify-content:center;gap:8px;transition:all .2s;}
  .pay-btn.active{background:${GRAD_H};background-size:200% 100%;animation:gradSpin 4s ease infinite;color:#fff;cursor:pointer;box-shadow:0 8px 22px ${HEART.redSoft};}
  .pay-btn.active:hover{transform:translateY(-2px);box-shadow:0 10px 26px rgba(200,32,46,.28);}
  .pay-btn.inactive{background:${T.mutedSoft};color:#fff;cursor:not-allowed;}

  .shein-check{width:19px;height:19px;border-radius:7px;border:1.5px solid ${T.gold};background:#fff;display:flex;align-items:center;justify-content:center;flex-shrink:0;cursor:pointer;transition:all .15s;}
  .shein-check.on{background:${T.red};border-color:${T.red};}

  .step-card{background:#fff;border:1.5px solid ${T.border};border-radius:22px;margin-bottom:14px;overflow:hidden;animation:slideUp .28s ease both;box-shadow:0 3px 16px rgba(27,14,6,.05);}
  .step-card-header{display:flex;align-items:center;gap:12px;padding:16px 18px;border-bottom:1px solid ${T.border};cursor:pointer;user-select:none;transition:background .14s;}
  .step-card-header:hover{background:${T.bg};}
  .step-card-body{padding:20px 18px;}

  .fi{width:100%;padding:11px 13px;border:1.5px solid ${T.border};background:#fff;font-family:inherit;font-size:13px;color:${T.ink};outline:none;transition:border-color .15s,box-shadow .15s;border-radius:14px;}
  .fi:focus{border-color:${T.red};box-shadow:0 0 0 3px ${HEART.redSoft};}
  .fl{display:block;font-family:inherit;font-size:10px;font-weight:800;color:${T.muted};letter-spacing:0.12em;text-transform:uppercase;margin-bottom:6px;}
  .fs{width:100%;padding:11px 13px;border:1.5px solid ${T.border};background:#fff;font-family:inherit;font-size:13px;color:${T.ink};outline:none;appearance:none;cursor:pointer;border-radius:14px;}
  .fs:focus{border-color:${T.red};}

  .step-num{width:26px;height:26px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-family:inherit;font-size:11px;font-weight:800;flex-shrink:0;}
  .step-num.done{background:${T.ink};color:#fff;}
  .step-num.active{background:${GRAD_H};background-size:200% 100%;animation:gradSpin 4s ease infinite;color:#fff;}
  .step-num.pending{background:${T.border};color:${T.muted};}

  .size-chip{padding:9px 17px;border:1.5px solid ${T.border};background:#fff;border-radius:999px;font-family:inherit;font-size:12px;font-weight:700;color:${T.muted};cursor:pointer;transition:all .15s;}
  .size-chip:hover{border-color:${T.gold};color:${T.ink};}
  .size-chip.on{background:${T.red};border-color:${T.red};color:#fff;box-shadow:0 4px 12px ${HEART.redSoft};}

  .bfs-toggle{display:flex;align-items:center;gap:10px;padding:13px 15px;border:1.5px dashed ${T.mutedSoft};cursor:pointer;transition:all .15s;background:transparent;width:100%;border-radius:16px;}
  .bfs-toggle:hover{border-color:${T.red};}
  .bfs-toggle.on{border-style:solid;border-color:${T.red};background:${T.bgSoft};}

  .country-dropdown{position:absolute;top:calc(100% + 6px);left:0;right:0;z-index:500;background:#fff;border:1.5px solid ${T.border};border-radius:16px;max-height:220px;overflow-y:auto;box-shadow:0 12px 32px rgba(27,14,6,.14);}
  .country-option{display:flex;align-items:center;gap:10px;padding:10px 13px;cursor:pointer;font-family:inherit;font-size:12px;color:${T.ink};transition:background .12s;border-bottom:1px solid ${T.border};}
  .country-option:hover{background:${T.bg};}
  .dial-dropdown{position:absolute;top:calc(100% + 6px);left:0;z-index:500;background:#fff;border:1.5px solid ${T.border};border-radius:16px;width:220px;max-height:220px;overflow-y:auto;box-shadow:0 12px 32px rgba(27,14,6,.14);}
  .dial-option{display:flex;align-items:center;gap:10px;padding:10px 13px;cursor:pointer;font-family:inherit;font-size:12px;color:${T.ink};transition:background .12s;border-bottom:1px solid ${T.border};}
  .dial-option:hover{background:${T.bg};}

  .bc-step{font-size:11px;font-weight:700;color:${T.mutedSoft};letter-spacing:0.1em;text-transform:uppercase;}
  .bc-step.active{color:${T.red};font-weight:900;}
  .bc-sep{color:${T.mutedSoft};font-size:10px;margin:0 6px;}

  .pay-method-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:16px;}
  .pay-method-card{border:2px solid ${T.border};background:#fff;border-radius:20px;padding:16px 12px;cursor:pointer;display:flex;flex-direction:column;align-items:center;gap:8px;transition:all .2s;position:relative;overflow:hidden;user-select:none;}
  .pay-method-card:hover{border-color:${T.gold};background:${T.bgSoft};transform:translateY(-2px);}
  .pay-method-card.card.selected{border-color:${T.red};background:${HEART.redSoft};box-shadow:0 8px 22px ${HEART.redSoft};transform:translateY(-2px);}
  .pay-method-card.mpesa.selected{border-color:${T.green};background:rgba(21,128,61,.06);box-shadow:0 8px 22px rgba(21,128,61,.16);transform:translateY(-2px);}
  .pay-method-top-bar{position:absolute;top:0;left:0;right:0;height:4px;opacity:0;transition:opacity .18s;}
  .pay-method-card.selected .pay-method-top-bar{opacity:1;}
  .pay-method-check{position:absolute;top:8px;right:8px;width:17px;height:17px;border-radius:50%;border:1.5px solid ${T.border};background:#fff;display:flex;align-items:center;justify-content:center;transition:all .15s;}
  .pay-method-card.card.selected  .pay-method-check{background:${T.red};border-color:${T.red};}
  .pay-method-card.mpesa.selected .pay-method-check{background:${T.green};border-color:${T.green};}
  .pay-method-label{font-size:10px;font-weight:800;letter-spacing:.1em;text-transform:uppercase;color:${T.ink};text-align:center;}
  .pay-method-sub{font-size:9px;color:${T.muted};text-align:center;line-height:1.4;}

  .pay-btn-card{width:100%;padding:15px 0;border:none;border-radius:999px;font-size:13px;font-weight:800;letter-spacing:.04em;font-family:inherit;display:flex;align-items:center;justify-content:center;gap:8px;transition:all .2s;}
  .pay-btn-card.active{background:${GRAD_H};background-size:200% 100%;animation:gradSpin 4s ease infinite;color:#fff;cursor:pointer;box-shadow:0 8px 22px ${HEART.redSoft};}
  .pay-btn-card.active:hover{transform:translateY(-2px);}
  .pay-btn-card.inactive{background:${T.mutedSoft};color:#fff;cursor:not-allowed;}

  .pay-btn-mpesa{width:100%;padding:15px 0;border:none;border-radius:999px;font-size:13px;font-weight:800;letter-spacing:.04em;font-family:inherit;display:flex;align-items:center;justify-content:center;gap:8px;transition:all .2s;}
  .pay-btn-mpesa.active{background:${T.green};color:#fff;cursor:pointer;box-shadow:0 8px 22px rgba(21,128,61,.25);}
  .pay-btn-mpesa.active:hover{transform:translateY(-2px);box-shadow:0 10px 26px rgba(21,128,61,.32);}
  .pay-btn-mpesa.inactive{background:rgba(21,128,61,.14);color:rgba(21,128,61,.45);cursor:not-allowed;}

  .fulf-grid{display:grid;gap:12px;margin-bottom:22px;}
  @media(max-width:580px){.fulf-grid{grid-template-columns:repeat(2,1fr) !important;}}

  .rate-row{border:1.5px solid ${T.border};padding:13px 15px;cursor:pointer;display:flex;align-items:center;gap:12px;transition:border-color .15s,background .15s;background:#fff;border-radius:16px;margin-bottom:9px;}
  .rate-row:hover{border-color:${T.gold};background:${T.bgSoft};}
  .rate-row.active{border-color:${T.red};background:${HEART.redSoft};}
`;

/* ── Shared sub-components ── */
const FeeRow: React.FC<{
  label: string; value: string; sub?: string;
  bold?: boolean; muted?: boolean; green?: boolean; red?: boolean; loading?: boolean;
}> = ({ label, value, sub, bold, muted, green, red, loading }) => (
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: `1px solid ${T.border}` }}>
    <div>
      <div style={{ fontSize: bold ? 14 : 13, fontWeight: bold ? 800 : 500, color: muted ? T.muted : T.ink }}>{label}</div>
      {sub && <div style={{ fontSize: 10, color: T.muted, marginTop: 1 }}>{sub}</div>}
    </div>
    {loading
      ? <span style={{ fontSize: 11, color: T.muted }}>Calculating…</span>
      : <span style={{ fontSize: bold ? 16 : 13, fontWeight: bold ? 900 : 600, color: red ? T.red : green ? T.green : T.ink }}>{value}</span>}
  </div>
);

const StepBadge: React.FC<{ n: number; current: number }> = React.memo(({ n, current }) => (
  <div className={`step-num${n < current ? " done" : n === current ? " active" : " pending"}`}>
    {n < current ? <Check size={12} color="#fff" /> : n}
  </div>
));

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div><label className="fl">{label}</label>{children}</div>
);

/* ═══════════════════════════════════════════════════════════
   CART ITEM ROW — cute rounded-rectangle product thumbnail
═══════════════════════════════════════════════════════════ */
const CartItemRow: React.FC<{
  item: any; listing: any; index: number; sym: string;
  checked: boolean; onCheck: () => void;
  onInc: () => void; onDec: () => void; onRemove: () => void; onNavigate: () => void;
}> = React.memo(({ item, listing, index, sym, checked, onCheck, onInc, onDec, onRemove, onNavigate }) => {
  const [removing, setRemoving] = useState(false);
  const [wished,   setWished]   = useState(false);
  const colorAttr = getColorAttr(item.colorId);
  const colorHex  = colorAttr?.hexCodes?.[0] ?? null;
  const colorName = colorAttr?.name ?? null;
  const sizeName  = getSizeName(item.sizeId);
  const previewImg = item.colorId && listing?.variants?.[item.colorId]?.imageUrl
    ? listing.variants[item.colorId].imageUrl
    : listing?.photos?.[0] ?? item.photo;
  const handleRemove = () => { setRemoving(true); setTimeout(onRemove, 300); };
  return (
    <motion.div className="ci-row"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: removing ? 0 : 1, y: removing ? -6 : 0 }}
      transition={{ duration: .28, delay: removing ? 0 : index * .05 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 9, padding: "10px 16px", borderBottom: `1px solid ${T.border}`, background: T.bg }}>
        <div className={`shein-check${checked ? " on" : ""}`} onClick={onCheck}>{checked && <Check size={11} color="#fff" />}</div>
        <span style={{ fontSize: 12, fontWeight: 800, color: T.ink }}>{listing?.brandName ?? listing?.sellerName ?? "Store"}</span>
        <ChevronRight size={12} color={T.muted} style={{ marginLeft: "auto" }} />
      </div>
      <div style={{ display: "flex", gap: 0, padding: "14px 16px" }}>
        <div className={`shein-check${checked ? " on" : ""}`} onClick={onCheck} style={{ marginTop: 4, marginRight: 10 }}>{checked && <Check size={11} color="#fff" />}</div>
        <div onClick={onNavigate} className="ci-thumb" style={{ width: 100, height: 124, flexShrink: 0, background: T.bgSoft, cursor: "pointer", marginRight: 14 }}>
          {previewImg
            ? <img src={previewImg} alt={item.name} style={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform .35s" }}
                onMouseEnter={e => (e.currentTarget.style.transform = "scale(1.08)")}
                onMouseLeave={e => (e.currentTarget.style.transform = "scale(1)")} />
            : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}><Package size={24} color={T.mutedSoft} /></div>}
        </div>
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: T.ink, lineHeight: 1.4, cursor: "pointer", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" as any, overflow: "hidden" }} onClick={onNavigate}>{item.name}</div>
          {(colorName || sizeName) && (
            <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
              {colorHex && <div style={{ width: 19, height: 19, borderRadius: "50%", flexShrink: 0, background: sphere3D(colorHex), boxShadow: "inset -4px -3px 8px rgba(0,0,0,.30),inset 3px 3px 6px rgba(255,255,255,.18),0 3px 10px rgba(0,0,0,.18)" }} />}
              <span style={{ fontSize: 11, color: T.muted, background: T.bgSoft, padding: "3px 10px", borderRadius: 999, border: `1px solid ${T.border}` }}>{[colorName, sizeName].filter(Boolean).join(" / ")}</span>
            </div>
          )}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "auto", paddingTop: 6 }}>
            <div style={{ display: "flex", alignItems: "center", border: `1.5px solid ${T.border}`, borderRadius: 999, overflow: "hidden" }}>
              <button onClick={onDec} style={{ width: 30, height: 30, border: "none", background: T.bg, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: T.red, fontSize: 15, fontWeight: 700 }}>−</button>
              <div style={{ width: 36, height: 30, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: T.ink }}>{item.qty}</div>
              <button onClick={onInc} style={{ width: 30, height: 30, border: "none", background: T.bg, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: T.red, fontSize: 15, fontWeight: 700 }}>+</button>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 16, fontWeight: 900, color: T.ink }}>{fmtPrice(item.qty * item.rate, sym)}</div>
{item.qty > 1 && <div style={{ fontSize: 10, color: T.muted }}>{fmtPrice(item.rate, sym)} each</div>}
              </div>
              <button className="ib" onClick={() => setWished(v => !v)}><Heart size={13} color={wished ? T.red : T.muted} fill={wished ? T.red : "none"} /></button>
              <button className="ib danger" onClick={handleRemove}><Trash2 size={13} color={T.red} /></button>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
});

/* ═══════════════════════════════════════════════════════════
   COUNTRY / DIAL PICKERS
═══════════════════════════════════════════════════════════ */
const CountryField: React.FC<{ value: string; onChange: (c: CountryInfo) => void }> = React.memo(({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = React.useRef<HTMLDivElement>(null);
  const selected = countries.find(c => c.name === value);
  const filtered = useMemo(() => { const q = query.toLowerCase(); return q ? countries.filter(c => c.name.toLowerCase().includes(q) || c.phoneCode.includes(q)) : countries; }, [query]);
  useEffect(() => { const fn = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); }; document.addEventListener("mousedown", fn); return () => document.removeEventListener("mousedown", fn); }, []);
  return (
    <div ref={ref} style={{ position: "relative" }}>
      <div onMouseDown={e => { e.preventDefault(); setOpen(o => !o); setQuery(""); }}
        style={{ display: "flex", alignItems: "center", gap: 9, padding: "11px 13px", border: `1.5px solid ${open ? T.red : T.border}`, background: "#fff", cursor: "pointer", borderRadius: 14, boxShadow: open ? `0 0 0 3px ${HEART.redSoft}` : "none" }}>
        {selected ? <>{flagSphere(selected.emoji)}<span style={{ fontSize: 13, color: T.ink, flex: 1 }}>{selected.name}</span></> : <span style={{ fontSize: 13, color: T.mutedSoft, flex: 1 }}>Select country</span>}
        <ChevronDown size={13} color={T.muted} style={{ transition: "transform .15s", transform: open ? "rotate(180deg)" : "none" }} />
      </div>
      <AnimatePresence>
        {open && (
          <motion.div className="country-dropdown" onMouseDown={e => e.preventDefault()} initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }} transition={{ duration: .14 }}>
            <div style={{ padding: "9px 11px", borderBottom: `1px solid ${T.border}`, position: "sticky", top: 0, background: "#fff" }}>
              <input autoFocus placeholder="Search country…" value={query} onChange={e => setQuery(e.target.value)} className="fi" style={{ borderRadius: 10, fontSize: 12, padding: "7px 10px" }} />
            </div>
            {filtered.slice(0, 80).map(c => (
              <div key={c.code} className="country-option" onClick={() => { onChange(c); setOpen(false); setQuery(""); }}>
                {flagSphere(c.emoji)}<span style={{ flex: 1 }}>{c.name}</span><span style={{ color: T.muted, fontSize: 11 }}>{c.phoneCode}</span>
              </div>
            ))}
            {filtered.length === 0 && <div style={{ padding: "14px 12px", fontSize: 12, color: T.muted }}>No results</div>}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

const DialField: React.FC<{ dialCode: string; onChange: (code: string) => void }> = React.memo(({ dialCode, onChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = React.useRef<HTMLDivElement>(null);
  const selected = countries.find(c => c.phoneCode === dialCode);
  const filtered = useMemo(() => { const q = query.toLowerCase(); return q ? countries.filter(c => c.name.toLowerCase().includes(q) || c.phoneCode.includes(q)) : countries; }, [query]);
  useEffect(() => { const fn = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); }; document.addEventListener("mousedown", fn); return () => document.removeEventListener("mousedown", fn); }, []);
  return (
    <div ref={ref} style={{ position: "relative", flexShrink: 0 }}>
      <div onMouseDown={e => { e.preventDefault(); setOpen(o => !o); setQuery(""); }}
        style={{ display: "flex", alignItems: "center", gap: 7, padding: "11px 11px", border: `1.5px solid ${T.border}`, borderRight: "none", background: T.bgSoft, cursor: "pointer", minWidth: 92, borderRadius: "14px 0 0 14px" }}>
        {selected && flagSphere(selected.emoji)}
        <span style={{ fontSize: 12, color: T.ink }}>{dialCode}</span>
        <ChevronDown size={11} color={T.muted} />
      </div>
      <AnimatePresence>
        {open && (
          <motion.div className="dial-dropdown" onMouseDown={e => e.preventDefault()} initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }} transition={{ duration: .14 }}>
            <div style={{ padding: "8px 9px", borderBottom: `1px solid ${T.border}`, position: "sticky", top: 0, background: "#fff" }}>
              <input autoFocus placeholder="Search…" value={query} onChange={e => setQuery(e.target.value)} className="fi" style={{ borderRadius: 10, fontSize: 11, padding: "6px 8px" }} />
            </div>
            {filtered.slice(0, 80).map(c => (
              <div key={c.code} className="dial-option" onClick={() => { onChange(c.phoneCode); setOpen(false); setQuery(""); }}>
                {flagSphere(c.emoji)}<span style={{ flex: 1, fontSize: 11 }}>{c.name}</span><span style={{ color: T.muted, fontSize: 11, fontWeight: 700 }}>{c.phoneCode}</span>
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

/* ═══════════════════════════════════════════════════════════
   TYPES
═══════════════════════════════════════════════════════════ */
type AddrState = {
  firstName: string; lastName: string; email: string;
  country: string; state: string; city: string; postalCode: string;
  address: string; dialCode: string; phone: string;
};

type MeasState = {
  blazerLength: string; hips: string; skirtLength: string; waist: string;
  armhole: string; bust: string; shoulderLength: string; sleeveLength: string;
  preferredFit: string; specialInstructions: string;
};

type CarrierKey = "dhl" | "xafarii" | "fba" | "fbs";
type PaymentMethod = "card" | "mpesa";

/* ═══════════════════════════════════════════════════════════
   ████████████████████████████████████████████████████████
   STEP TOGGLE CONFIGURATION
   ────────────────────────────────────────────────────────
   Comment out any step below to hide it from the checkout
   flow. The step numbers and breadcrumb update automatically.
   The "sizes" step is additionally auto-hidden whenever none
   of the checked cart items are fashion (clothing / shoes).
═══════════════════════════════════════════════════════════ */
const ACTIVE_STEPS = [
  "cart",
  "delivery",
  "sizes",
  "measurements",
  "shipping",
] as const;
type StepKey = typeof ACTIVE_STEPS[number];

/* ═══════════════════════════════════════════════════════════
   STEP 1: CART
═══════════════════════════════════════════════════════════ */
interface CartStepProps {
  items: any[]; listings: any[]; stepIdx: number; stepNumber: number; sym: string;
  checkedItems: Set<string>;
  onToggleAll: () => void; onToggleItem: (key: string) => void;
  onContinue: () => void;
  onInc: (item: any) => void; onDec: (item: any) => void;
  onRemove: (item: any) => void; onNavigate: (id: string) => void;
}
const CartStep: React.FC<CartStepProps> = React.memo(({
  items, listings, stepIdx, stepNumber, sym, checkedItems, onToggleAll, onToggleItem,
  onContinue, onInc, onDec, onRemove, onNavigate,
}) => {
  const getListing  = (id: string) => (listings as any[]).find((l: any) => l.listingId === id);
  const itemKey     = (item: any) => `${item.listingId}-${item.colorId ?? "nc"}-${item.sizeId ?? "ns"}`;
  const allChecked  = items.length > 0 && items.every(i => checkedItems.has(itemKey(i)));
  const someChecked = items.some(i => checkedItems.has(itemKey(i)));
  const checkedSubtotal = items.filter(i => checkedItems.has(itemKey(i))).reduce((s: number, i: any) => s + i.qty * i.rate, 0);
  return (
    <div className="step-card">
      <div className="step-card-header">
        <StepBadge n={stepNumber} current={stepIdx + 1} />
        <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase" as const, color: T.ink }}>Cart Items</span>
        <span style={{ marginLeft: "auto", fontSize: 11, color: T.muted }}>{items.length} item{items.length !== 1 ? "s" : ""}</span>
      </div>
      {stepIdx === 0 && (
        <div style={{ padding: "14px 18px 18px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "11px 15px", background: T.bgSoft, borderRadius: 16, marginBottom: 14 }}>
            <div className={`shein-check${allChecked ? " on" : ""}`} onClick={onToggleAll}>
              {allChecked && <Check size={11} color="#fff" />}
              {!allChecked && someChecked && <div style={{ width: 8, height: 2, background: T.gold, borderRadius: 1 }} />}
            </div>
            <span style={{ fontSize: 13, fontWeight: 800, color: T.ink }}>All items ({items.length})</span>
            {someChecked && !allChecked && (
              <span style={{ marginLeft: "auto", fontSize: 11, color: T.red, fontWeight: 700 }}>
                {items.filter(i => checkedItems.has(itemKey(i))).length} selected · {fmtPrice(checkedSubtotal, sym)}
              </span>
            )}
          </div>
          {items.map((item: any, i: number) => {
            const key = itemKey(item);
            return (
              <CartItemRow key={key} item={item} listing={getListing(item.listingId)} index={i} sym={sym}
                checked={checkedItems.has(key)} onCheck={() => onToggleItem(key)}
                onInc={() => onInc(item)} onDec={() => onDec(item)}
                onRemove={() => onRemove(item)} onNavigate={() => onNavigate(item.listingId)} />
            );
          })}
          <button onClick={onContinue} className={`pay-btn${someChecked ? " active" : " inactive"}`} disabled={!someChecked}>
            Continue <ChevronRight size={14} />
          </button>
        </div>
      )}
      {stepIdx > 0 && (
        <div style={{ padding: "12px 18px", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" as const }}>
          {items.filter(i => checkedItems.has(itemKey(i))).slice(0, 3).map((item: any) => (
            <div key={itemKey(item)} className="ci-thumb" style={{ width: 44, height: 56, background: T.bgMuted, flexShrink: 0 }}>
              {item.photo && <img src={item.photo} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />}
            </div>
          ))}
          {items.filter(i => checkedItems.has(itemKey(i))).length > 3 && (
            <span style={{ fontSize: 11, color: T.muted }}>+{items.filter(i => checkedItems.has(itemKey(i))).length - 3} more</span>
          )}
        </div>
      )}
    </div>
  );
});

/* ═══════════════════════════════════════════════════════════
   STEP 2: DELIVERY
═══════════════════════════════════════════════════════════ */
interface DeliveryStepProps {
  stepIdx: number; myIdx: number; stepNumber: number; isLoggedIn: boolean; initialAddr: AddrState;
  buyForSomeoneElse: boolean; onToggleBuyForSomeoneElse: () => void;
  onFlush: (patch: Partial<AddrState>) => void;
  onContinue: (latest: AddrState) => void; onBack: () => void;
}
const DeliveryStep: React.FC<DeliveryStepProps> = React.memo(({
  stepIdx, myIdx, stepNumber, isLoggedIn, initialAddr, buyForSomeoneElse, onToggleBuyForSomeoneElse,
  onFlush, onContinue, onBack,
}) => {
  const [country,  setCountry]  = useState(initialAddr.country);
  const [dialCode, setDialCode] = useState(initialAddr.dialCode);
  const prevInit = useRef(initialAddr);
  useEffect(() => {
    if (prevInit.current !== initialAddr) {
      prevInit.current = initialAddr;
      setCountry(initialAddr.country);
      setDialCode(initialAddr.dialCode);
    }
  }, [initialAddr]);
  const rFirstName = useRef<HTMLInputElement>(null);
  const rLastName  = useRef<HTMLInputElement>(null);
  const rEmail     = useRef<HTMLInputElement>(null);
  const rState     = useRef<HTMLInputElement>(null);
  const rCity      = useRef<HTMLInputElement>(null);
  const rPostal    = useRef<HTMLInputElement>(null);
  const rAddress   = useRef<HTMLInputElement>(null);
  const rPhone     = useRef<HTMLInputElement>(null);
  const handleCountry = (c: CountryInfo) => { setCountry(c.name); setDialCode(c.phoneCode); onFlush({ country: c.name, dialCode: c.phoneCode }); };
  const handleDial    = (v: string) => { setDialCode(v); onFlush({ dialCode: v }); };
  const blurField = (key: keyof AddrState) => (e: React.FocusEvent<HTMLInputElement>) => { onFlush({ [key]: e.target.value }); };
  const blurPhone = (e: React.FocusEvent<HTMLInputElement>) => {
    const clean = stripDialCode(e.target.value, dialCode);
    if (e.target.value !== clean) e.target.value = clean;
    onFlush({ phone: clean });
  };
  const handleContinue = () => {
    const latest: AddrState = {
      firstName: rFirstName.current?.value ?? "", lastName: rLastName.current?.value ?? "",
      email: rEmail.current?.value ?? "", state: rState.current?.value ?? "",
      city: rCity.current?.value ?? "", postalCode: rPostal.current?.value ?? "",
      address: rAddress.current?.value ?? "",
      phone: stripDialCode(rPhone.current?.value ?? "", dialCode),
      country, dialCode,
    };
    if (rPhone.current && rPhone.current.value !== latest.phone) rPhone.current.value = latest.phone;
    onContinue(latest);
  };
  return (
    <div className="step-card">
      <div className="step-card-header" onClick={stepIdx > myIdx ? onBack : undefined}>
        <StepBadge n={stepNumber} current={stepIdx + 1} />
        <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase" as const, color: T.ink }}>Contact & Delivery</span>
        {stepIdx > myIdx && <Check size={14} color={T.green} style={{ marginLeft: "auto" }} />}
      </div>
      {stepIdx === myIdx && (
        <div className="step-card-body">
          {isLoggedIn && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, background: "rgba(21,128,61,0.06)", border: "1px solid rgba(21,128,61,0.2)", padding: "10px 13px", marginBottom: 16, borderRadius: 14 }}>
              <Check size={12} color={T.green} /><p style={{ fontSize: 11, color: T.green }}>Pre-filled from your profile — all fields are still editable.</p>
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
            <Field label="First Name *"><input ref={rFirstName} className="fi" placeholder="e.g. Alex" defaultValue={initialAddr.firstName} onBlur={blurField("firstName")} /></Field>
            <Field label="Last Name *"><input ref={rLastName} className="fi" placeholder="e.g. Smith" defaultValue={initialAddr.lastName} onBlur={blurField("lastName")} /></Field>
          </div>
          <div style={{ marginBottom: 12 }}>
            <Field label="Email Address *">
              <div style={{ position: "relative" }}>
                <input ref={rEmail} className="fi" placeholder="your@email.com" defaultValue={initialAddr.email} onBlur={blurField("email")} style={{ paddingLeft: 38 }} />
                <Mail size={13} color={T.muted} style={{ position: "absolute", left: 13, top: "50%", transform: "translateY(-50%)" }} />
              </div>
            </Field>
          </div>
          <div style={{ marginBottom: 16 }}>
            <button className={`bfs-toggle${buyForSomeoneElse ? " on" : ""}`} onClick={onToggleBuyForSomeoneElse}>
              <div className={`shein-check${buyForSomeoneElse ? " on" : ""}`}>{buyForSomeoneElse && <Check size={10} color="#fff" />}</div>
              <Users size={13} color={buyForSomeoneElse ? T.ink : T.muted} />
              <div style={{ textAlign: "left" as const }}>
                <p style={{ fontSize: 11, fontWeight: 800, color: buyForSomeoneElse ? T.ink : T.muted, letterSpacing: "0.08em" }}>BUY FOR SOMEONE ELSE</p>
                <p style={{ fontSize: 10, color: T.muted, marginTop: 2 }}>Ship to a different person — we'll set up an account for them</p>
              </div>
            </button>
            <AnimatePresence>
              {buyForSomeoneElse && (
                <motion.div initial={{ opacity: 0, y: -8, height: 0 }} animate={{ opacity: 1, y: 0, height: "auto" }} exit={{ opacity: 0, y: -8, height: 0 }} transition={{ duration: .2 }}
                  style={{ marginTop: 10, padding: 15, background: T.bgSoft, border: `1px solid ${T.border}`, overflow: "hidden", borderRadius: 16 }}>
                  <p style={{ fontSize: 10, color: T.muted, marginBottom: 12, letterSpacing: "0.1em" }}>RECIPIENT DETAILS — we'll create an account using this email</p>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
                    <Field label="Recipient First Name *"><input className="fi" placeholder="Their first name" defaultValue={initialAddr.firstName} onBlur={blurField("firstName")} /></Field>
                    <Field label="Recipient Last Name *"><input className="fi" placeholder="Their last name" defaultValue={initialAddr.lastName} onBlur={blurField("lastName")} /></Field>
                  </div>
                  <Field label="Recipient Email *">
                    <div style={{ position: "relative" }}>
                      <input className="fi" placeholder="recipient@email.com" defaultValue={initialAddr.email} onBlur={blurField("email")} style={{ paddingLeft: 38 }} />
                      <Mail size={13} color={T.muted} style={{ position: "absolute", left: 13, top: "50%", transform: "translateY(-50%)" }} />
                    </div>
                  </Field>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
            <Field label="Country *"><CountryField value={country} onChange={handleCountry} /></Field>
            <Field label="State / Province"><input ref={rState} className="fi" placeholder="e.g. California" defaultValue={initialAddr.state} onBlur={blurField("state")} /></Field>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
            <Field label="City *"><input ref={rCity} className="fi" placeholder="e.g. New York" defaultValue={initialAddr.city} onBlur={blurField("city")} /></Field>
            <Field label="Postal Code *"><input ref={rPostal} className="fi" placeholder="e.g. 10001" defaultValue={initialAddr.postalCode} onBlur={blurField("postalCode")} /></Field>
          </div>
          <div style={{ marginBottom: 12 }}>
            <Field label="Delivery Address *">
              <div style={{ position: "relative" }}>
                <input ref={rAddress} className="fi" placeholder="Street, apartment, building" defaultValue={initialAddr.address} onBlur={blurField("address")} style={{ paddingLeft: 38 }} />
                <HomeIcon size={13} color={T.muted} style={{ position: "absolute", left: 13, top: "50%", transform: "translateY(-50%)" }} />
              </div>
            </Field>
          </div>
          <div style={{ marginBottom: 20 }}>
            <Field label="WhatsApp Number *">
              <div style={{ display: "flex" }}>
                <DialField dialCode={dialCode} onChange={handleDial} />
                <input ref={rPhone} className="fi" placeholder="e.g. 7001234567"
                  defaultValue={stripDialCode(initialAddr.phone, initialAddr.dialCode)}
                  onBlur={blurPhone} style={{ flex: 1, borderRadius: "0 14px 14px 0" }} inputMode="tel" />
              </div>
              <p style={{ fontSize: 10, color: T.muted, marginTop: 6 }}>{dialCode} + your number · dial code added automatically</p>
            </Field>
          </div>
          <button onClick={handleContinue} className="pay-btn active">Continue <ChevronRight size={14} /></button>
        </div>
      )}
      {stepIdx > myIdx && (
        <div style={{ padding: "12px 18px", display: "flex", alignItems: "center", gap: 8 }}>
          {country && (() => { const c = countries.find(x => x.name === country); return c ? flagSphere(c.emoji) : null; })()}
          <p style={{ fontSize: 12, color: T.muted }}>
            {initialAddr.firstName} {initialAddr.lastName} · {initialAddr.city}, {country}
            {buyForSomeoneElse && <span style={{ marginLeft: 6, fontSize: 10, color: T.gold, fontWeight: 800 }}>· GIFT</span>}
          </p>
        </div>
      )}
    </div>
  );
});

/* ═══════════════════════════════════════════════════════════
   STEP 3: SIZES — only ever rendered when the checkout has at
   least one fashion item (clothing / shoes). Non-fashion carts
   never reach this step at all.
═══════════════════════════════════════════════════════════ */
interface SizesStepProps {
  stepIdx: number; myIdx: number; stepNumber: number; items: any[]; listings: any[];
  selectedSizes: Record<string, number>;
  onSelectSize: (key: string, sizeId: number) => void;
  onContinue: () => void;
}
const SizesStep: React.FC<SizesStepProps> = React.memo(({ stepIdx, myIdx, stepNumber, items, listings, selectedSizes, onSelectSize, onContinue }) => {
  const getListing   = (id: string) => (listings as any[]).find((l: any) => l.listingId === id);
  const fashionItems = items.filter((item: any) => isFashionItem(item, getListing(item.listingId)));
  const sizeName = (id?: number) => (id ? SIZE_VALUES.find((v: any) => v.id === id)?.name : undefined);
  return (
    <div className="step-card">
      <div className="step-card-header">
        <StepBadge n={stepNumber} current={stepIdx + 1} />
        <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase" as const, color: T.ink }}>Sizes</span>
        {stepIdx > myIdx && <Check size={14} color={T.green} style={{ marginLeft: "auto" }} />}
      </div>
      {stepIdx === myIdx && (
        <div className="step-card-body">
          <p style={{ fontSize: 12, color: T.muted, marginBottom: 18 }}>Select your size for each item — only sizes available for the chosen colour are shown.</p>
          {fashionItems.map((item: any) => {
            const listing   = getListing(item.listingId);
            const key       = cartItemKey(item);
            const chosenId  = selectedSizes[key];
            const available = getAvailableSizesForItem(item, listing);
            return (
              <div key={key} style={{ marginBottom: 18, paddingBottom: 18, borderBottom: `1px solid ${T.border}` }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
                  <div className="ci-thumb" style={{ width: 42, height: 54, background: T.bgMuted, flexShrink: 0 }}>
                    {item.photo && <img src={item.photo} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />}
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 600, color: T.ink, lineHeight: 1.3, flex: 1 }}>{item.name}</span>
                  {chosenId != null && (
                    <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: T.green, fontWeight: 800, flexShrink: 0 }}>
                      <Check size={11} color={T.green} />{sizeName(chosenId)}
                    </span>
                  )}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 9 }}>
                  <Ruler size={12} color={T.muted} />
                  <span style={{ fontSize: 10, color: T.muted, letterSpacing: "0.08em", textTransform: "uppercase" as const, fontWeight: 800 }}>Available sizes</span>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 8 }}>
                  {available.map(s => (
                    <button
                      key={s.id}
                      className={`size-chip${chosenId === s.id ? " on" : ""}`}
                      onClick={() => onSelectSize(key, s.id)}
                    >
                      {s.name}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
          <button onClick={onContinue} className="pay-btn active" style={{ marginTop: 4 }}>Continue <ChevronRight size={14} /></button>
        </div>
      )}
      {stepIdx > myIdx && (
        <div style={{ padding: "12px 18px" }}>
          <p style={{ fontSize: 12, color: T.muted }}>
            {Object.values(selectedSizes).filter(Boolean).length > 0
              ? Object.values(selectedSizes).map(id => sizeName(id)).filter(Boolean).join(", ")
              : "No sizes selected"}
          </p>
        </div>
      )}
    </div>
  );
});

/* ═══════════════════════════════════════════════════════════
   STEP 4: MEASUREMENTS
═══════════════════════════════════════════════════════════ */
interface MeasurementsStepProps {
  stepIdx: number; myIdx: number; stepNumber: number; initialMeas: MeasState;
  onFlush: (patch: Partial<MeasState>) => void;
  onContinue: (latest: MeasState) => void;
}
const MeasurementsStep: React.FC<MeasurementsStepProps> = React.memo(({ stepIdx, myIdx, stepNumber, initialMeas, onFlush, onContinue }) => {
  const [fit, setFit] = useState(initialMeas.preferredFit);
  const prevInit = useRef(initialMeas);
  useEffect(() => {
    if (prevInit.current !== initialMeas) { prevInit.current = initialMeas; setFit(initialMeas.preferredFit); }
  }, [initialMeas]);
  const refs = {
    blazerLength: useRef<HTMLInputElement>(null), hips: useRef<HTMLInputElement>(null),
    skirtLength:  useRef<HTMLInputElement>(null), waist: useRef<HTMLInputElement>(null),
    armhole:      useRef<HTMLInputElement>(null), bust: useRef<HTMLInputElement>(null),
    shoulderLength: useRef<HTMLInputElement>(null), sleeveLength: useRef<HTMLInputElement>(null),
    specialInstructions: useRef<HTMLTextAreaElement>(null),
  };
  const blurField = (key: keyof Omit<MeasState, "preferredFit">) =>
    (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => { onFlush({ [key]: e.target.value }); };
  const handleContinue = () => {
    const latest: MeasState = {
      blazerLength: refs.blazerLength.current?.value ?? "", hips: refs.hips.current?.value ?? "",
      skirtLength:  refs.skirtLength.current?.value  ?? "", waist: refs.waist.current?.value ?? "",
      armhole:      refs.armhole.current?.value       ?? "", bust: refs.bust.current?.value ?? "",
      shoulderLength: refs.shoulderLength.current?.value ?? "", sleeveLength: refs.sleeveLength.current?.value ?? "",
      specialInstructions: refs.specialInstructions.current?.value ?? "", preferredFit: fit,
    };
    onContinue(latest);
  };
  const MEAS_FIELDS: [keyof Omit<MeasState, "preferredFit" | "specialInstructions">, string][] = [
    ["blazerLength","Full Blazer Length"],["hips","Hips"],["skirtLength","Skirt Length"],["waist","Waist"],
    ["armhole","Arm Hole"],["bust","Bust"],["shoulderLength","Shoulder Length"],["sleeveLength","Sleeve Length"],
  ];
  return (
    <div className="step-card">
      <div className="step-card-header">
        <StepBadge n={stepNumber} current={stepIdx + 1} />
        <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase" as const, color: T.ink }}>Measurements</span>
        {stepIdx > myIdx && <Check size={14} color={T.green} style={{ marginLeft: "auto" }} />}
      </div>
      {stepIdx === myIdx && (
        <div className="step-card-body">
          <div style={{ background: T.bgSoft, border: `1px solid ${T.border}`, padding: "11px 13px", marginBottom: 18, borderRadius: 14 }}>
            <p style={{ fontSize: 11, color: T.goldDark }}>Provide your measurements for custom tailoring (optional). All measurements in inches.</p>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
            {MEAS_FIELDS.map(([k, label]) => (
              <Field key={k} label={label}>
                <input ref={refs[k] as React.RefObject<HTMLInputElement>} className="fi" placeholder="e.g. 34"
                  defaultValue={initialMeas[k]} onBlur={blurField(k)} />
              </Field>
            ))}
          </div>
          <div style={{ marginBottom: 12 }}>
            <Field label="Preferred Fit">
              <div style={{ position: "relative" }}>
                <select className="fs" value={fit} onChange={e => { setFit(e.target.value); onFlush({ preferredFit: e.target.value }); }}>
                  {FIT_OPTIONS.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
                <ChevronDown size={13} color={T.muted} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }} />
              </div>
            </Field>
          </div>
          <div style={{ marginBottom: 20 }}>
            <Field label="Special Instructions">
              <textarea ref={refs.specialInstructions} className="fi" placeholder="Any special fitting requirements…"
                rows={4} defaultValue={initialMeas.specialInstructions} onBlur={blurField("specialInstructions")}
                style={{ resize: "vertical", minHeight: 90 }} />
            </Field>
          </div>
          <button onClick={handleContinue} className="pay-btn active">Continue <ChevronRight size={14} /></button>
        </div>
      )}
      {stepIdx > myIdx && (
        <div style={{ padding: "12px 18px" }}>
          <p style={{ fontSize: 12, color: T.muted }}>Fit: {fit} · {Object.values(initialMeas).some((v: any) => v && v !== "Regular") ? "Measurements provided" : "No measurements"}</p>
        </div>
      )}
    </div>
  );
});

/* ═══════════════════════════════════════════════════════════
   STEP 5: SHIPPING
═══════════════════════════════════════════════════════════ */
interface ShippingStepProps {
  stepIdx: number; activeStepIdx: number; stepNumber: number;
  addr: AddrState; subtotal: number; totalQty: number; sym: string;
  grandTotal: number; canPay: boolean;
  payingCard: boolean; payingMpesa: boolean; payError: string | null;
  onPayCard: () => void; onPayMpesa: () => void; onDismissError: () => void;
  selectedPayment: PaymentMethod; onSelectPayment: (p: PaymentMethod) => void;
  brandDhlRates: Record<string, any[]>; brandDhlLoading: Record<string, boolean>;
  brandDhlErrors: Record<string, string | null>; selectedBrandRates: Record<string, any>;
  onSelectBrandRate: (brandId: string, rate: any) => void; fetchBrandDhlRates: () => void;
  checkedBrandIds: string[];
  selectedFulfillment: "FBS" | "FBP" | "FBA"; onSelectFulfillment: (f: "FBS" | "FBP" | "FBA") => void;
  selectedCarrier: CarrierKey; onSelectCarrier: (c: CarrierKey) => void;
  dhlRates: any[]; dhlLoading: boolean; dhlError: string | null;
  selectedDhlRate: any; onSelectDhlRate: (r: any) => void; fetchDhlRates: () => void;
  safaRates: any[]; safaLoading: boolean; safaError: string | null;
  selectedSafaRate: any; onSelectSafaRate: (r: any) => void; fetchSafaRates: () => void;
  fbaRates: any[]; fbaLoading: boolean; fbaError: string | null;
  selectedFbaRate: any; onSelectFbaRate: (r: any) => void; fetchFbaRates: () => void;
}

const ShippingStep: React.FC<ShippingStepProps> = React.memo(({
  stepIdx, activeStepIdx, stepNumber, addr, subtotal, totalQty, sym,
  grandTotal, canPay, payingCard, payingMpesa, payError,
  onPayCard, onPayMpesa, onDismissError,
  selectedPayment, onSelectPayment,
  brandDhlRates, brandDhlLoading, brandDhlErrors, selectedBrandRates, onSelectBrandRate,
  fetchBrandDhlRates, checkedBrandIds,
  selectedFulfillment, onSelectFulfillment,
  selectedCarrier, onSelectCarrier,
  dhlRates, dhlLoading, dhlError, selectedDhlRate, onSelectDhlRate, fetchDhlRates,
  safaRates, safaLoading, safaError, selectedSafaRate, onSelectSafaRate, fetchSafaRates,
  fbaRates, fbaLoading, fbaError, selectedFbaRate, onSelectFbaRate, fetchFbaRates,
}) => {
  const paying    = payingCard || payingMpesa;
  const fbsTotal  = checkedBrandIds.reduce((sum, bid) => { const r = selectedBrandRates[bid]; return sum + (r ? (r.totalPrice ?? r.price ?? 0) : 0); }, 0);
  const dhlTotal  = selectedDhlRate  ? (selectedDhlRate.totalPrice  ?? 0) : 0;
  const safaTotal = selectedSafaRate ? (selectedSafaRate.totalPrice ?? 0) : 0;
  const fbaTotal  = selectedFbaRate  ? (selectedFbaRate.totalPrice  ?? 0) : 0;
  const fbpTotal  = selectedCarrier === "dhl" ? dhlTotal : safaTotal;
  const deliveryFee = selectedFulfillment === "FBS" ? fbsTotal : selectedFulfillment === "FBP" ? fbpTotal : fbaTotal;

  type CarrierDef = { key: CarrierKey; logo: string; label: string; topBar: string; fee: number | null };
  const CARRIERS: CarrierDef[] = [
    { key: "fbs", logo: fbsLogo, label: "FBS · Seller",
      topBar: GRAD_H,
      fee: checkedBrandIds.some(bid => selectedBrandRates[bid]) ? fbsTotal : null },
  ];

  const [collapsed, setCollapsed] = useState(false);

  return (
    <div className="step-card">
      <div className="step-card-header" onClick={() => setCollapsed(v => !v)} style={{ cursor: "pointer" }}>
        <StepBadge n={stepNumber} current={stepIdx + 1} />
        <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase" as const, color: T.ink }}>Delivery Method</span>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
          {collapsed && (
            <span style={{ fontSize: 11, color: T.muted, fontWeight: 700 }}>
              {selectedCarrier.toUpperCase()} · {fmtPrice(deliveryFee, sym)}
            </span>
          )}
          <ChevronDown size={14} color={T.muted} style={{ transition: "transform .2s", transform: collapsed ? "rotate(0deg)" : "rotate(180deg)" }} />
        </div>
      </div>

      <AnimatePresence initial={false}>
        {!collapsed && stepIdx === activeStepIdx && (
          <motion.div key="shipping-body"
            initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }} transition={{ duration: .25, ease: [.4, 0, .2, 1] }}
            style={{ overflow: "hidden" }}>
            <div className="step-card-body">

              <div className="fulf-grid" style={{ gridTemplateColumns: `repeat(${CARRIERS.length}, 1fr)` }}>
                {CARRIERS.map(c => {
                  const sel = selectedCarrier === c.key;
                  return (
                    <div key={c.key}
                      onClick={() => onSelectCarrier(c.key)}
                      style={{
                        position: "relative", borderRadius: 20, overflow: "hidden", cursor: "pointer", aspectRatio: "3/4",
                        border: `2.5px solid ${sel ? T.red : "transparent"}`,
                        boxShadow: sel ? `0 10px 28px ${HEART.redSoft}` : "0 2px 12px rgba(27,14,6,0.08)",
                        transform: sel ? "translateY(-4px) scale(1.02)" : "translateY(0) scale(1)",
                        transition: "all .22s cubic-bezier(.4,0,.2,1)", userSelect: "none",
                      }}>
                      <img src={c.logo} alt={c.label}
                        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", transition: "transform .3s", transform: sel ? "scale(1.06)" : "scale(1)" }}
                        onError={e => { e.currentTarget.style.display = "none"; }} />
                      <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(27,14,6,0.82) 0%, rgba(27,14,6,0.18) 55%, rgba(27,14,6,0) 100%)", pointerEvents: "none" }} />
                      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 4, background: c.topBar, opacity: sel ? 1 : 0, transition: "opacity .2s" }} />
                      <div style={{ position: "absolute", top: 9, right: 9, width: 20, height: 20, borderRadius: "50%", background: sel ? T.red : "rgba(255,255,255,0.18)", border: `1.5px solid ${sel ? T.red : "rgba(255,255,255,0.4)"}`, display: "flex", alignItems: "center", justifyContent: "center", transition: "all .18s", backdropFilter: "blur(4px)" }}>
                        {sel && <Check size={10} color="#fff" />}
                      </div>
                      <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "11px 11px 13px", display: "flex", flexDirection: "column", gap: 3 }}>
                        <span style={{ fontSize: 11, fontWeight: 900, letterSpacing: ".1em", textTransform: "uppercase" as const, color: "#fff", textShadow: "0 1px 4px rgba(0,0,0,0.5)" }}>{c.label}</span>
                        <span style={{ fontSize: 13, fontWeight: 900, color: sel ? T.gold : "#fff", textShadow: "0 1px 6px rgba(0,0,0,0.6)" }}>
                          {c.fee !== null ? fmtPrice(c.fee, sym) : <span style={{ fontSize: 10, opacity: 0.6 }}>—</span>}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>

              <AnimatePresence mode="wait">
                {selectedFulfillment === "FBS" && selectedCarrier === "fbs" && (
                  <motion.div key="fbs-rates" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: .22 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14, padding: "12px 15px", background: T.bgSoft, border: `1px solid ${T.border}`, borderRadius: 16 }}>
                      <img src={fbsLogo} alt="FBS" style={{ height: 26, objectFit: "contain" }} onError={e => { e.currentTarget.style.display = "none"; }} />
                      <div>
                        <p style={{ fontSize: 12, fontWeight: 800, color: T.ink }}>Fulfilled by Seller</p>
                        <p style={{ fontSize: 11, color: T.muted }}>Each brand ships independently to {addr.city || addr.country || "your address"}</p>
                      </div>
                    </div>
                    {checkedBrandIds.map(bid => {
                      const rates   = brandDhlRates[bid] ?? [];
                      const loading = brandDhlLoading[bid] ?? false;
                      const error   = brandDhlErrors[bid]  ?? null;
                      const selRate = selectedBrandRates[bid];
                      return (
                        <div key={bid} style={{ marginBottom: 14, border: `1px solid ${T.border}`, borderRadius: 16, overflow: "hidden" }}>
                          <div style={{ padding: "9px 14px", background: T.bg, borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 8 }}>
                            <Truck size={12} color={T.muted} />
                            <span style={{ fontSize: 11, fontWeight: 800, color: T.ink }}>Brand {bid.slice(0, 8)}…</span>
                          </div>
                          <div style={{ padding: "11px 14px" }}>
                            {loading && <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 0" }}><span style={{ width: 13, height: 13, border: `2px solid ${T.border}`, borderTopColor: T.gold, borderRadius: "50%", animation: "pcSpin 1s linear infinite", display: "inline-block" }} /><span style={{ fontSize: 11, color: T.muted }}>Fetching rates…</span></div>}
                            {error && !loading && <div style={{ display: "flex", alignItems: "center", gap: 8 }}><AlertCircle size={12} color={T.red} /><span style={{ fontSize: 11, color: T.red, flex: 1 }}>{error}</span><button onClick={fetchBrandDhlRates} style={{ fontSize: 10, color: T.red, background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}>Retry</button></div>}
                            {!loading && !error && rates.length === 0 && <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0" }}><Truck size={12} color={T.green} /><span style={{ fontSize: 11, color: T.green }}>Free delivery for this seller</span></div>}
                            {!loading && rates.map((rate: any, i: number) => {
                              const isSel = selRate?.productCode === rate.productCode;
                              const price = rate.totalPrice ?? rate.price ?? 0;
                              const days  = rate.deliveryCapabilities?.estimatedDeliveryDateAndTime ?? rate.deliveryTime ?? "2–5 days";
                              return (
                                <div key={i} className={`rate-row${isSel ? " active" : ""}`} onClick={() => onSelectBrandRate(bid, rate)}>
                                  <div style={{ width: 15, height: 15, borderRadius: "50%", border: `2px solid ${isSel ? T.red : T.border}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                    {isSel && <div style={{ width: 6, height: 6, borderRadius: "50%", background: T.red }} />}
                                  </div>
                                  <img src={fbsLogo} alt="" style={{ height: 18, objectFit: "contain", flexShrink: 0 }} onError={e => { e.currentTarget.style.display = "none"; }} />
                                  <div style={{ flex: 1 }}>
                                    <p style={{ fontSize: 12, fontWeight: 700, color: T.ink }}>{rate.productName ?? "Seller Shipping"}</p>
                                    <p style={{ fontSize: 10, color: T.muted }}>{typeof days === "string" ? days : "Est. delivery available"}</p>
                                  </div>
                                  <span style={{ fontSize: 13, fontWeight: 800, color: price === 0 ? T.green : T.ink }}>{price === 0 ? "Free" : fmtPrice(price, sym)}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                    {checkedBrandIds.some(bid => selectedBrandRates[bid]) && (
                      <div style={{ display: "flex", justifyContent: "space-between", padding: "11px 15px", background: T.bgSoft, border: `1px solid ${T.border}`, borderRadius: 16, marginBottom: 16 }}>
                        <span style={{ fontSize: 12, fontWeight: 800, color: T.ink }}>Total Shipping (all sellers)</span>
                        <span style={{ fontSize: 14, fontWeight: 900, color: T.ink }}>{fmtPrice(fbsTotal, sym)}</span>
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>

              <div style={{ background: T.bgSoft, border: `1px solid ${T.border}`, borderRadius: 16, padding: "14px 16px", marginBottom: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: `1px solid ${T.border}` }}>
                  <span style={{ fontSize: 12, color: T.muted }}>Items subtotal</span>
                  <span style={{ fontSize: 12, fontWeight: 700 }}>{fmtPrice(subtotal, sym)}</span>
                </div>
<div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: `1px solid ${T.border}` }}>
                  <span style={{ fontSize: 12, color: T.muted }}>
                    Delivery
                    <span style={{ marginLeft: 6, fontSize: 9, fontWeight: 800, color: T.goldDark, background: T.bgSoft, padding: "1px 6px", borderRadius: 999 }}>{selectedFulfillment}</span>
                  </span>
                  <span style={{ fontSize: 12, fontWeight: 700 }}>{fmtPrice(deliveryFee, sym)}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "9px 0 2px" }}>
                  <span style={{ fontSize: 14, fontWeight: 800, color: T.ink }}>Total</span>
                  <span style={{ fontSize: 18, fontWeight: 900, color: T.red }}>
                    {fmtPrice(grandTotal + deliveryFee, sym)}
                  </span>
                </div>
              </div>

              <div style={{ marginBottom: 14 }}>
                <p style={{ fontSize: 10, fontWeight: 800, color: T.muted, letterSpacing: ".14em", textTransform: "uppercase" as const, marginBottom: 10 }}>Choose payment method</p>
                <div className="pay-method-grid">
                  <div className={`pay-method-card card${selectedPayment === "card" ? " selected" : ""}`} onClick={() => onSelectPayment("card")}>
                    <div className="pay-method-top-bar" style={{ background: GRAD_H }} />
                    <div className="pay-method-check">{selectedPayment === "card" && <Check size={9} color="#fff" />}</div>
                    <CreditCard size={26} color={selectedPayment === "card" ? T.red : T.muted} />
                    <span className="pay-method-label" style={{ color: selectedPayment === "card" ? T.red : undefined }}>Card</span>
                    <span className="pay-method-sub">Debit &amp; credit cards</span>
                  </div>
                  <div className={`pay-method-card mpesa${selectedPayment === "mpesa" ? " selected" : ""}`} onClick={() => onSelectPayment("mpesa")}>
                    <div className="pay-method-top-bar" style={{ background: T.green }} />
                    <div className="pay-method-check">{selectedPayment === "mpesa" && <Check size={9} color="#fff" />}</div>
                    <Smartphone size={26} color={selectedPayment === "mpesa" ? T.green : T.muted} />
                    <span className="pay-method-label" style={{ color: selectedPayment === "mpesa" ? T.green : undefined }}>M-Pesa</span>
                    <span className="pay-method-sub">Mobile money</span>
                  </div>
                </div>
              </div>

              <AnimatePresence mode="wait">
                {selectedPayment === "card" ? (
                  <motion.button key="card-btn"
                    initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }} transition={{ duration: .18 }}
                    whileHover={canPay ? { scale: 1.01 } as any : {}}
                    whileTap={canPay ? { scale: .98 } as any : {}}
                    onClick={onPayCard}
                    className={`pay-btn-card${canPay ? " active" : " inactive"}`}>
                    {payingCard ? <span style={{ width: 13, height: 13, border: "2px solid rgba(255,255,255,.6)", borderTopColor: "transparent", borderRadius: "50%", animation: "pcSpin 1s linear infinite" }} /> : <Lock size={13} />}
                    {payingCard ? "Processing…" : `Pay ${fmtPrice(grandTotal + deliveryFee, sym)}`}
                  </motion.button>
                ) : (
                  <motion.button key="mpesa-btn"
                    initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }} transition={{ duration: .18 }}
                    whileHover={canPay ? { scale: 1.01 } as any : {}}
                    whileTap={canPay ? { scale: .98 } as any : {}}
                    onClick={onPayMpesa}
                    className={`pay-btn-mpesa${canPay ? " active" : " inactive"}`}>
                    {payingMpesa ? <span style={{ width: 13, height: 13, border: "2px solid rgba(255,255,255,.6)", borderTopColor: "transparent", borderRadius: "50%", animation: "pcSpin 1s linear infinite" }} /> : <Smartphone size={13} />}
                    {payingMpesa ? "Connecting to M-Pesa…" : `Pay with M-Pesa  ${fmtPrice(grandTotal + deliveryFee, sym)}`}
                  </motion.button>
                )}
              </AnimatePresence>

              <AnimatePresence>
                {payError && (
                  <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                    style={{ display: "flex", alignItems: "flex-start", gap: 8, background: HEART.redSoft, border: "1px solid rgba(200,32,46,.25)", padding: "12px 14px", marginTop: 10, borderRadius: 14 }}>
                    <AlertCircle size={13} color={T.red} style={{ flexShrink: 0, marginTop: 1 }} />
                    <span style={{ fontSize: 11, color: T.red, flex: 1 }}>{payError}</span>
                    <button onClick={onDismissError} style={{ background: "none", border: "none", padding: 0 }}><X size={12} color={T.red} /></button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════ */
const ProductCheckout: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate   = useNavigate();
  const dispatch   = useDispatch<AppDispatch>();

  const items      = useSelector((s: RootState) => s.cart.items);
  const listings   = useSelector((s: RootState) => s.products.products);
  
  const fees = useSelector((s: RootState) => s.productServiceFee);
  const user       = useSelector((s: RootState) => s.auth.user);
  const isLoggedIn = !!user;

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", fn); return () => window.removeEventListener("resize", fn);
  }, []);

  const [userCoords, setUserCoords] = useState<{ lat: number; lng: number } | null>(null);
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      pos => setUserCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      ()  => setUserCoords(null),
    );
  }, []);

  const [step, setStep] = useState<StepKey>("cart");

  const [addr, setAddr] = useState<AddrState>({
    firstName: "", lastName: "", email: "",
    country: "", state: "", city: "", postalCode: "",
    address: "", dialCode: "+254", phone: "",
  });
  const patchAddr = useCallback((patch: Partial<AddrState>) => setAddr(p => ({ ...p, ...patch })), []);

  useEffect(() => {
    if (!user) return;
    const ship = user.shippingAddress;
    const email = user.contactEmail || user.email || user.emailAddress || "";
    const rawPhone  = user.contactNumber || user.whatsappNumber || "";
    const dialMatch = rawPhone.match(/^(\+\d{1,4})\s?/);
    const dialCode  = dialMatch ? dialMatch[1] : (ship?.countryCode ? `+${ship.countryCode}` : "+254");
    const phone     = dialMatch ? rawPhone.replace(dialMatch[0], "") : rawPhone;
    setAddr({ firstName: user.firstName || "", lastName: user.lastName || (user as any).surName || "", email, country: ship?.country || "", state: ship?.state || "", city: ship?.city || "", postalCode: ship?.zipCode || "", address: ship?.streetAddress || "", dialCode, phone });
  }, [user]);

  const [checkedItems, setCheckedItems] = useState<Set<string>>(new Set());
  useEffect(() => { setCheckedItems(new Set(items.map((i: any) => `${i.listingId}-${i.colorId ?? "nc"}-${i.sizeId ?? "ns"}`))); }, []);
  const [buyForSomeoneElse, setBuyForSomeoneElse] = useState(false);

  const [selectedSizes, setSelectedSizes] = useState<Record<string, number>>({});
  useEffect(() => {
    if (!user?.sizes || !items.length) return;
    const defaultSzName = (user.sizes as any).top || (user.sizes as any).dress || "";
    if (!defaultSzName) return;
    const defaultSz = SIZE_VALUES.find((v: any) => v.name === defaultSzName);
    if (!defaultSz) return;
    setSelectedSizes(prev => {
      const next = { ...prev };
      items.forEach((item: any) => {
        const key = cartItemKey(item);
        if (!next[key]) next[key] = defaultSz.id;
      });
      return next;
    });
  }, [user, items]);
  const handleSelectSize = useCallback((key: string, sizeId: number) => {
    setSelectedSizes(p => ({ ...p, [key]: sizeId }));
  }, []);

  const [measurements, setMeasurements] = useState<MeasState>({
    blazerLength: "", hips: "", skirtLength: "", waist: "",
    armhole: "", bust: "", shoulderLength: "", sleeveLength: "",
    preferredFit: "Regular", specialInstructions: "",
  });
  useEffect(() => {
    if (!user?.sizes) return;
    setMeasurements(p => ({ ...p, bust: (user.sizes as any)?.bust || p.bust, waist: (user.sizes as any)?.waist || p.waist, hips: (user.sizes as any)?.hips || p.hips }));
  }, [user]);
  const patchMeas = useCallback((patch: Partial<MeasState>) => setMeasurements(p => ({ ...p, ...patch })), []);

  const checkedItemsList = useMemo(() =>
    items.filter((i: any) => checkedItems.has(`${i.listingId}-${i.colorId ?? "nc"}-${i.sizeId ?? "ns"}`)),
  [items, checkedItems]);

  const getListingById = useCallback((id: string) => (listings as any[]).find((l: any) => l.listingId === id), [listings]);

  /* Sizes step only exists when at least one checked item is fashion (clothing / shoes) */
  const hasFashionItems = useMemo(
    () => checkedItemsList.some((i: any) => isFashionItem(i, getListingById(i.listingId))),
    [checkedItemsList, getListingById]
  );
  const visibleSteps = useMemo(
    () => ACTIVE_STEPS.filter(s => s !== "sizes" || hasFashionItems),
    [hasFashionItems]
  );
  const stepIdx  = Math.max(0, visibleSteps.indexOf(step));
  const nextStep = (from: StepKey): StepKey => visibleSteps[visibleSteps.indexOf(from) + 1] ?? "shipping";

  const checkedBrandIds = useMemo(() =>
    Array.from(new Set(checkedItemsList.map((i: any) => i.brandId ?? i.hostId).filter(Boolean))),
  [checkedItemsList]);

  const [brandDhlRates,    setBrandDhlRates]    = useState<Record<string, any[]>>({});
  const [brandDhlLoading,  setBrandDhlLoading]  = useState<Record<string, boolean>>({});
  const [brandDhlErrors,   setBrandDhlErrors]   = useState<Record<string, string | null>>({});
  const [selectedBrandRates, setSelectedBrandRates] = useState<Record<string, any>>({});

  const fetchBrandDhlRates = useCallback(async () => {
    if (!userCoords) return;
    for (const bid of checkedBrandIds) {
      setBrandDhlLoading(p => ({ ...p, [bid]: true }));
      setBrandDhlErrors(p  => ({ ...p, [bid]: null  }));
      try {
        const result = await dispatch(nearestDeliveryLocation({
          brandId: bid,
          userLatitude:  userCoords.lat,
          userLongitude: userCoords.lng,
        })).unwrap();
        const syntheticRate = result.matchedZone ? {
          productCode:  "FBS",
          productName:  result.matchedZone.type === "local"
            ? "Local Delivery"
            : result.matchedZone.type === "regional"
            ? "Regional Delivery"
            : "International Delivery",
          totalPrice:   result.deliveryFee,
          deliveryTime: `${result.matchedZone.deliveryTime ?? 1}–${(result.matchedZone.deliveryTime ?? 1) + 1} days`,
        } : null;
        setBrandDhlRates(p => ({ ...p, [bid]: syntheticRate ? [syntheticRate] : [] }));
        if (syntheticRate) setSelectedBrandRates(p => ({ ...p, [bid]: syntheticRate }));
      } catch {
        setBrandDhlErrors(p => ({ ...p, [bid]: "Could not fetch delivery rate for this seller." }));
        setBrandDhlRates(p  => ({ ...p, [bid]: [] }));
      }
      setBrandDhlLoading(p => ({ ...p, [bid]: false }));
    }
  }, [userCoords, checkedBrandIds, dispatch]);

  const onSelectBrandRate = useCallback((brandId: string, rate: any) => setSelectedBrandRates(p => ({ ...p, [brandId]: rate })), []);

  const [dhlRates, setDhlRates]     = useState<any[]>([]);
  const [dhlLoading, setDhlLoading] = useState(false);
  const [dhlError, setDhlError]     = useState<string | null>(null);
  const [selectedDhlRate, setSelectedDhlRate] = useState<any | null>(null);

  const fetchDhlRates = useCallback(async () => {
    if (!addr.country || !addr.postalCode) return;
    setDhlLoading(true); setDhlError(null);
    try {
      const res = await axios.post(DHL_RATES_URL, { toCountry: addr.country, toPostalCode: addr.postalCode, toCity: addr.city, weight: Math.max(0.5, checkedItemsList.reduce((s: number, i: any) => s + (i.qty || 1), 0) * 0.4), network: "FBP" });
      const d = res.data as any;
      const rates = Array.isArray(d) ? d : d?.products ?? [];
      setDhlRates(rates);
      if (rates.length > 0) { const cheapest = [...rates].sort((a: any, b: any) => (a.totalPrice ?? 0) - (b.totalPrice ?? 0))[0]; setSelectedDhlRate(cheapest); }
    } catch { setDhlError("Could not fetch DHL rates. Please try again."); }
    setDhlLoading(false);
  }, [addr.country, addr.postalCode, addr.city, checkedItemsList]);

  const [safaRates, setSafaRates]     = useState<any[]>([]);
  const [safaLoading, setSafaLoading] = useState(false);
  const [safaError, setSafaError]     = useState<string | null>(null);
  const [selectedSafaRate, setSelectedSafaRate] = useState<any | null>(null);

  const fetchSafaRates = useCallback(async () => {
    if (!addr.country || !addr.postalCode) return;
    setSafaLoading(true); setSafaError(null);
    try {
      const res = await axios.post(XAFARII_RATES_URL, { toCountry: addr.country, toPostalCode: addr.postalCode, toCity: addr.city, weight: Math.max(0.5, checkedItemsList.reduce((s: number, i: any) => s + (i.qty || 1), 0) * 0.4) });
      const d = res.data as any;
      const rates = Array.isArray(d) ? d : d?.products ?? [];
      setSafaRates(rates);
      if (rates.length > 0) { const cheapest = [...rates].sort((a: any, b: any) => (a.totalPrice ?? 0) - (b.totalPrice ?? 0))[0]; setSelectedSafaRate(cheapest); }
    } catch { setSafaError("Could not fetch Xafarii rates. Please try again."); }
    setSafaLoading(false);
  }, [addr.country, addr.postalCode, addr.city, checkedItemsList]);

  const [fbaRates, setFbaRates]     = useState<any[]>([]);
  const [fbaLoading, setFbaLoading] = useState(false);
  const [fbaError, setFbaError]     = useState<string | null>(null);
  const [selectedFbaRate, setSelectedFbaRate] = useState<any | null>(null);

  const fetchFbaRates = useCallback(async () => {
    if (!addr.country || !addr.postalCode) return;
    setFbaLoading(true); setFbaError(null);
    try {
      const res = await axios.post(FBA_RATES_URL, { toCountry: addr.country, toPostalCode: addr.postalCode, toCity: addr.city, weight: Math.max(0.5, checkedItemsList.reduce((s: number, i: any) => s + (i.qty || 1), 0) * 0.4), subtotal: fees?.subtotal ?? 0 });
      const d = res.data as any;
      const rates = Array.isArray(d) ? d : d?.products ?? [];
      setFbaRates(rates);
      if (rates.length > 0) { const cheapest = [...rates].sort((a: any, b: any) => (a.totalPrice ?? 0) - (b.totalPrice ?? 0))[0]; setSelectedFbaRate(cheapest); }
    } catch { setFbaError("Could not fetch FBA rates. Please try again."); }
    setFbaLoading(false);
  }, [addr.country, addr.postalCode, addr.city, checkedItemsList, fees]);

  const onSelectFbaRate = useCallback((rate: any) => setSelectedFbaRate(rate), []);

  const [selectedFulfillment, setSelectedFulfillment] = useState<"FBS" | "FBP" | "FBA">("FBS");
  const [selectedCarrier,     setSelectedCarrier]     = useState<CarrierKey>("fbs");

  const handleSelectCarrier = useCallback((c: CarrierKey) => {
    setSelectedCarrier(c);
    if (c === "dhl")     { setSelectedFulfillment("FBP"); fetchDhlRates(); }
    if (c === "xafarii") { setSelectedFulfillment("FBP"); fetchSafaRates(); }
    if (c === "fba")     { setSelectedFulfillment("FBA"); fetchFbaRates(); }
    if (c === "fbs")     { setSelectedFulfillment("FBS"); }
  }, [fetchDhlRates, fetchSafaRates, fetchFbaRates]);

  
const subtotal = useMemo(
  () => checkedItemsList.reduce((s: number, i: any) => s + (i.rate ?? 0) * (i.qty ?? 1), 0),
  [checkedItemsList]
);
const totalQty = useMemo(
  () => checkedItemsList.reduce((s: number, i: any) => s + (i.qty ?? 1), 0),
  [checkedItemsList]
);
const { guestsFee, totalCharged } = fees;

  const fbsTotal      = checkedBrandIds.reduce((sum, bid) => { const r = selectedBrandRates[bid]; return sum + (r ? (r.totalPrice ?? r.price ?? 0) : 0); }, 0);
  const dhlTotalMain  = selectedDhlRate  ? (selectedDhlRate.totalPrice  ?? 0) : 0;
  const safaTotalMain = selectedSafaRate ? (selectedSafaRate.totalPrice ?? 0) : 0;
  const fbaTotalMain  = selectedFbaRate  ? (selectedFbaRate.totalPrice  ?? 0) : 0;
  const fbpTotal      = selectedCarrier === "dhl" ? dhlTotalMain : safaTotalMain;
  const deliveryFee   = selectedFulfillment === "FBS" ? fbsTotal : selectedFulfillment === "FBP" ? fbpTotal : fbaTotalMain;
  const grandTotal    = (totalCharged || 0) + deliveryFee;

  const [payingCard,  setPayingCard]  = useState(false);
  const [payingMpesa, setPayingMpesa] = useState(false);
  const [payError,    setPayError]    = useState<string | null>(null);
  const [selectedPayment, setSelectedPayment] = useState<PaymentMethod>("card");
  const paying = payingCard || payingMpesa;
  const canPay = !paying && checkedItemsList.length > 0 && step === "shipping";

  useEffect(() => {
    if (step !== "shipping") return;
    if (!userCoords) return;
    if (selectedCarrier === "fbs" && checkedBrandIds.length > 0 &&
        Object.keys(selectedBrandRates).length === 0) {
      fetchBrandDhlRates();
    }
  }, [step, userCoords, checkedBrandIds.join(",")]);

  useEffect(() => { const ids = Array.from(new Set(items.map((i: any) => i.listingId))); ids.forEach((id: any) => dispatch(listenToProductData(id))); }, [items, dispatch]);
  useEffect(() => { dispatch(listenToPublishedProducts()); }, [dispatch]);

useEffect(() => { if (!checkedItemsList.length) return; dispatch(calculateProductsEarnings({ items: checkedItemsList })); }, [checkedItemsList, dispatch]);


  useEffect(() => { if (!items.length) navigate("/ProductHome"); }, [items, navigate]);

  const handleContinueDelivery     = useCallback((latest: AddrState)  => { setAddr(latest);         setStep(nextStep("delivery")); }, [visibleSteps]);
  const handleContinueMeasurements = useCallback((latest: MeasState)  => { setMeasurements(latest); setStep(nextStep("measurements")); fetchBrandDhlRates(); }, [fetchBrandDhlRates, visibleSteps]);

  const ensureCustomerAccount = async (): Promise<string> => {
    if (isLoggedIn && !buyForSomeoneElse) return user!.userId;
    const res = await axios.post<CreateCustomerResponse>(CREATE_CUSTOMER_URL, {
      firstName: addr.firstName, lastName: addr.lastName, email: addr.email,
      phone: `${addr.dialCode}${addr.phone}`,
      shippingAddress: { streetAddress: addr.address, city: addr.city, state: addr.state, country: addr.country, zipCode: addr.postalCode },
      buyForSomeoneElse, buyerUserId: isLoggedIn ? user!.userId : null,
    });
    if (!res.data?.userId) throw new Error("Account creation failed — no userId returned.");
    return res.data.userId;
  };

  const buildPayload = useCallback(async () => {
    const resolvedUserId = await ensureCustomerAccount();
    const itemRefs: string[] = checkedItemsList.map((item: any) =>
      `${item.listingId}:${item.hostId}:${item.colorId ?? "n"}:${item.sizeId ?? "n"}:${item.qty}`
    );
    const deliveredBy =
      selectedFulfillment === "FBS" ? selectedBrandRates
      : selectedFulfillment === "FBP" ? (selectedCarrier === "dhl" ? selectedDhlRate : selectedSafaRate)
      : selectedFbaRate;
    const sizesPayload: Record<string, string> = {};
    checkedItemsList.forEach((item: any) => {
      const key = cartItemKey(item);
      const sizeId = selectedSizes[key];
      if (!sizeId) return;
      const name = SIZE_VALUES.find((v: any) => v.id === sizeId)?.name;
      if (name) sizesPayload[item.listingId] = name;
    });
    return {
      resolvedUserId,
      body: {
        userId:            resolvedUserId,
        itemRefs,
        userLatitude:      userCoords?.lat ?? null,
        userLongitude:     userCoords?.lng ?? null,
        deliveredBy,
        measurements,
        selectedSizes:     sizesPayload,
        buyForSomeoneElse,
        buyerUserId:       user?.userId ?? null,
        fulfillmentMethod: selectedFulfillment,
      },
    };
  }, [checkedItemsList, selectedFulfillment, selectedCarrier, selectedBrandRates, selectedDhlRate, selectedSafaRate, selectedFbaRate, buyForSomeoneElse, selectedSizes, measurements, userCoords, user]);

  const redirectFromResponse = (data: { id?: string; url?: string; data?: { url?: string }; sessionUrl?: string; checkoutUrl?: string }) => {
    const directUrl = data?.url ?? data?.data?.url ?? data?.sessionUrl ?? data?.checkoutUrl;
    const sessionId = data?.id;
    if (directUrl)  { window.location.href = directUrl; return; }
    if (sessionId)  { window.location.href = `https://checkout.stripe.com/c/pay/${sessionId}`; return; }
    throw new Error(`No checkout URL or session ID returned. Server: ${JSON.stringify(data)}`);
  };

  const handlePayCard = useCallback(async () => {
    if (!canPay) return;
    setPayingCard(true); setPayError(null);
    try {
      const { body } = await buildPayload();
      const res = await axios.post<{ id?: string; url?: string; data?: { url?: string }; sessionUrl?: string; checkoutUrl?: string }>(
        CHECKOUT_URL, body
      );
      redirectFromResponse(res.data);
    } catch (e: any) {
      setPayError(e?.message ?? "Card payment failed. Please try again.");
      setPayingCard(false);
    }
  }, [canPay, buildPayload]);

  const handlePayMpesa = useCallback(async () => {
    if (!canPay) return;
    setPayingMpesa(true); setPayError(null);
    try {
      const { body } = await buildPayload();
      const res = await axios.post<{ id?: string; url?: string; data?: { url?: string }; sessionUrl?: string; checkoutUrl?: string }>(
        COSMOOLA_CHECKOUT_URL, body
      );
      const directUrl = res.data?.url ?? res.data?.data?.url ?? res.data?.sessionUrl ?? res.data?.checkoutUrl;
      if (!directUrl) throw new Error(`No Cosmoola URL returned. Server: ${JSON.stringify(res.data)}`);
      window.location.href = directUrl;
    } catch (e: any) {
      setPayError(e?.message ?? "M-Pesa payment failed. Please try again.");
      setPayingMpesa(false);
    }
  }, [canPay, buildPayload]);

  
  const handleInc = useCallback((item: any) => dispatch(addToCart({
  listingId: item.listingId, name: item.name, hostId: item.hostId,
  rate: item.rate, colorId: item.colorId, sizeId: item.sizeId, qty: 1, brandId: item.brandId
})), [dispatch]);

  const handleDec    = useCallback((item: any) => dispatch(decrementCartItem({ listingId: item.listingId, colorId: item.colorId, sizeId: item.sizeId })), [dispatch]);
  const handleRemove = useCallback((item: any) => dispatch(removeFromCart({ listingId: item.listingId, colorId: item.colorId, sizeId: item.sizeId })), [dispatch]);
  const handleNav    = useCallback((id: string) => navigate(`/ProductDetails/${id}`), [navigate]);

  const itemKey    = (item: any) => `${item.listingId}-${item.colorId ?? "nc"}-${item.sizeId ?? "ns"}`;
  const toggleItem = useCallback((key: string) => { setCheckedItems(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; }); }, []);
  const toggleAll  = useCallback(() => {
    if (items.every((i: any) => checkedItems.has(itemKey(i)))) setCheckedItems(new Set());
    else setCheckedItems(new Set(items.map((i: any) => itemKey(i))));
  }, [items, checkedItems]);

  const shippingStepIdx = visibleSteps.indexOf("shipping");
  const sym = "€";

  return (
    <div style={{ minHeight: "100vh", background: T.bg, fontFamily: "'Montserrat',sans-serif" }}>
      <style>{CSS}</style>

      {/* ── Header ── */}
      <header style={{ position: "sticky", top: 0, zIndex: 300, background: "#fff", borderBottom: `1px solid ${T.border}`, boxShadow: "0 2px 12px rgba(27,14,6,.05)" }}>
        <div style={{ height: 3, background: GRAD_H, backgroundSize: "200% 100%", animation: "gradSpin 4s ease infinite" }} />
        <div style={{ display: "flex", alignItems: "center", padding: "0 20px", height: 56, gap: 14 }}>
          <button onClick={() => navigate(-1)} style={{ background: "none", border: "none", color: T.muted, display: "flex", alignItems: "center", gap: 5, fontSize: 11, letterSpacing: ".1em", textTransform: "uppercase" as const }}>
            <ArrowLeft size={14} /> Back
          </button>
          <div style={{ flex: 1 }} />
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <ShoppingBag size={16} color={T.red} />
            <span style={{ fontSize: 12, color: T.ink, fontWeight: 800 }}>{totalQty}</span>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "8px 0 10px", borderTop: `1px solid ${T.border}` }}>
          {visibleSteps.map((s, i) => (
            <React.Fragment key={s}>
              <span className={`bc-step${s === step ? " active" : ""}`}>{s}</span>
              {i < visibleSteps.length - 1 && <span className="bc-sep">›</span>}
            </React.Fragment>
          ))}
        </div>
      </header>

      {/* ── Body ── */}
      <div style={{ maxWidth: 1080, margin: "0 auto", padding: isMobile ? "18px 14px 130px" : "30px 40px 60px", display: "grid", gridTemplateColumns: isMobile ? "1fr" : "minmax(0,1fr) 340px", gap: 28, alignItems: "start" }}>

        <div style={{ minWidth: 0 }}>

          <CartStep
            items={items} listings={listings as any[]} stepIdx={stepIdx} stepNumber={visibleSteps.indexOf("cart") + 1} sym={sym}
            checkedItems={checkedItems} onToggleAll={toggleAll} onToggleItem={toggleItem}
            onContinue={() => setStep(nextStep("cart"))}
            onInc={handleInc} onDec={handleDec} onRemove={handleRemove} onNavigate={handleNav}
          />

          {visibleSteps.includes("delivery") && stepIdx >= visibleSteps.indexOf("delivery") && (
            <DeliveryStep
              stepIdx={stepIdx} myIdx={visibleSteps.indexOf("delivery")} stepNumber={visibleSteps.indexOf("delivery") + 1}
              isLoggedIn={isLoggedIn} initialAddr={addr}
              buyForSomeoneElse={buyForSomeoneElse} onToggleBuyForSomeoneElse={() => setBuyForSomeoneElse(v => !v)}
              onFlush={patchAddr} onContinue={handleContinueDelivery} onBack={() => setStep("delivery")}
            />
          )}

          {visibleSteps.includes("sizes") && stepIdx >= visibleSteps.indexOf("sizes") && (
            <SizesStep
              stepIdx={stepIdx} myIdx={visibleSteps.indexOf("sizes")} stepNumber={visibleSteps.indexOf("sizes") + 1}
              items={checkedItemsList} listings={listings as any[]}
              selectedSizes={selectedSizes} onSelectSize={handleSelectSize}
              onContinue={() => setStep(nextStep("sizes"))}
            />
          )}

          {visibleSteps.includes("measurements") && stepIdx >= visibleSteps.indexOf("measurements") && (
            <MeasurementsStep
              stepIdx={stepIdx} myIdx={visibleSteps.indexOf("measurements")} stepNumber={visibleSteps.indexOf("measurements") + 1}
              initialMeas={measurements}
              onFlush={patchMeas} onContinue={handleContinueMeasurements}
            />
          )}

          {stepIdx >= shippingStepIdx && (
            <ShippingStep
              stepIdx={stepIdx} activeStepIdx={shippingStepIdx} stepNumber={visibleSteps.indexOf("shipping") + 1}
              addr={addr} subtotal={subtotal || 0} totalQty={totalQty || 0} sym={sym}
              grandTotal={totalCharged || 0}
              canPay={canPay}
              payingCard={payingCard} payingMpesa={payingMpesa} payError={payError}
              onPayCard={handlePayCard} onPayMpesa={handlePayMpesa}
              onDismissError={() => setPayError(null)}
              selectedPayment={selectedPayment} onSelectPayment={setSelectedPayment}
              brandDhlRates={brandDhlRates} brandDhlLoading={brandDhlLoading}
              brandDhlErrors={brandDhlErrors} selectedBrandRates={selectedBrandRates}
              onSelectBrandRate={onSelectBrandRate} fetchBrandDhlRates={fetchBrandDhlRates}
              checkedBrandIds={checkedBrandIds}
              selectedFulfillment={selectedFulfillment} onSelectFulfillment={setSelectedFulfillment}
              selectedCarrier={selectedCarrier} onSelectCarrier={handleSelectCarrier}
              dhlRates={dhlRates} dhlLoading={dhlLoading} dhlError={dhlError}
              selectedDhlRate={selectedDhlRate} onSelectDhlRate={setSelectedDhlRate} fetchDhlRates={fetchDhlRates}
              safaRates={safaRates} safaLoading={safaLoading} safaError={safaError}
              selectedSafaRate={selectedSafaRate} onSelectSafaRate={setSelectedSafaRate} fetchSafaRates={fetchSafaRates}
              fbaRates={fbaRates} fbaLoading={fbaLoading} fbaError={fbaError}
              selectedFbaRate={selectedFbaRate} onSelectFbaRate={onSelectFbaRate} fetchFbaRates={fetchFbaRates}
            />
          )}
        </div>

        {/* Right — order summary */}
        <div style={{ position: isMobile ? "static" : "sticky", top: 120, minWidth: 0 }}>
          <div style={{ background: "#fff", border: `1.5px solid ${T.border}`, borderRadius: 22, padding: "20px 18px", marginBottom: 14, boxShadow: "0 3px 16px rgba(27,14,6,.05)" }}>
            <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: ".22em", color: T.muted, marginBottom: 14 }}>ORDER SUMMARY</div>
            <FeeRow label="Subtotal"    value={fmtPrice(subtotal || 0, sym)} sub={`${totalQty} item${totalQty !== 1 ? "s" : ""}`} />
            <FeeRow label="Service Fee" value={fmtPrice(guestsFee || 0, sym)} sub="Platform fee" muted />
            <FeeRow label="Delivery"
              value={selectedFulfillment === "FBA"
                ? (selectedFbaRate ? fmtPrice(fbaTotalMain, sym) : "Select rate")
                : selectedFulfillment === "FBP"
                  ? (selectedCarrier === "dhl"
                      ? (selectedDhlRate  ? fmtPrice(dhlTotalMain,  sym) : "Select rate")
                      : (selectedSafaRate ? fmtPrice(safaTotalMain, sym) : "Select rate"))
                  : (checkedBrandIds.some(bid => selectedBrandRates[bid]) ? fmtPrice(fbsTotal, sym) : "Select rates")}
              loading={Object.values(brandDhlLoading).some(Boolean) || dhlLoading || safaLoading || fbaLoading}
            />
            {buyForSomeoneElse && <FeeRow label="Gift Order" value="Account created for recipient" green />}
            <div style={{ paddingTop: 12, display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
              <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: ".15em", color: T.ink }}>TOTAL</span>
              <span style={{ fontSize: 24, fontWeight: 900, color: T.red }}>{fmtPrice(grandTotal, sym)}</span>
            </div>
            <div style={{ fontSize: 10, color: T.muted, textAlign: "right", marginTop: 4 }}>All taxes included · Charged in EUR</div>
          </div>
        </div>
      </div>

      {/* Mobile sticky CTA */}
      {isMobile && (
        <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, background: "rgba(255,255,255,0.97)", borderTop: `1px solid ${T.border}`, padding: "14px 16px", zIndex: 200, backdropFilter: "blur(10px)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <span style={{ fontSize: 11, color: T.muted }}>Total</span>
            <span style={{ fontSize: 20, fontWeight: 900, color: T.ink }}>{fmtPrice(grandTotal, sym)}</span>
          </div>
          {step === "cart"         && <button onClick={() => setStep(nextStep("cart"))} className="pay-btn active">Continue <ChevronRight size={14} /></button>}
          {step === "delivery"     && <button onClick={() => { if (addr.firstName && addr.phone) handleContinueDelivery(addr); }} className={`pay-btn${addr.firstName && addr.phone ? " active" : " inactive"}`}>Continue <ChevronRight size={14} /></button>}
          {step === "sizes"        && <button onClick={() => setStep(nextStep("sizes"))} className="pay-btn active">Continue <ChevronRight size={14} /></button>}
          {step === "measurements" && <button onClick={() => handleContinueMeasurements(measurements)} className="pay-btn active">Continue <ChevronRight size={14} /></button>}
          {step === "shipping" && (
            selectedPayment === "card" ? (
              <motion.button whileTap={canPay ? { scale: .98 } as any : {}} onClick={handlePayCard} className={`pay-btn-card${canPay ? " active" : " inactive"}`}>
                {payingCard ? <span style={{ width: 13, height: 13, border: "2px solid rgba(255,255,255,.6)", borderTopColor: "transparent", borderRadius: "50%", animation: "pcSpin 1s linear infinite" }} /> : <Lock size={12} />}
                {payingCard ? "Processing…" : `Pay  ${fmtPrice(grandTotal, sym)}`}
              </motion.button>
            ) : (
              <motion.button whileTap={canPay ? { scale: .98 } as any : {}} onClick={handlePayMpesa} className={`pay-btn-mpesa${canPay ? " active" : " inactive"}`}>
                {payingMpesa ? <span style={{ width: 13, height: 13, border: "2px solid rgba(255,255,255,.6)", borderTopColor: "transparent", borderRadius: "50%", animation: "pcSpin 1s linear infinite" }} /> : <Smartphone size={12} />}
                {payingMpesa ? "Connecting…" : `M-Pesa  ${fmtPrice(grandTotal, sym)}`}
              </motion.button>
            )
          )}
        </div>
      )}
    </div>
  );
};

export default ProductCheckout;